import { createSelector, Selector, Store } from '@ngrx/store'
import { selectionFeature } from './selection.reducer'
import {
    findSelectionNodeInRoot,
    FreeSelection,
    getAllChildren,
    getAllChildrenPointsInRootOrNode,
    getAllChildrenSelectionTreeItems,
    getSelectionTreeItemLevel,
    HierarchicalSelectionNode,
    SelectionBase,
    SelectionLayerInfo,
    SelectionsForCaseList,
    SelectionState,
    SelectionTreeItem,
    SelectionType,
} from './selection.state'
import { SelectionLayer } from '../../shared/model/layers/selection-layer'

import { SegObjectPropertyMap } from '../case/case.state'
import { selectCaseSegObjectPropertyMap } from '../case/case.selectors'

export const defaultHierarchicalSelectionNodeItem: SelectionTreeItem = {
    name: undefined,
    level: 0,
    size: 0,
}

export const { selectSelectionState } = selectionFeature

export const {
    selectMode,
    selectFreeSelection,
    selectHierarchicalSelection,
    selectUnsupervisedSelection,
    selectSavedFreeSelections,
    selectSavedHierarchicalSelections,
    selectSavedUnsupervisedSelections,
} = selectionFeature

export const selectSelectionSize = createSelector(
    selectSelectionState,
    (state: SelectionState): number => {
        if (state.type == SelectionType.Hierarchical) {
            return state.hierarchicalSelection.unsavedLevel.points.size
        } else if (state.type == SelectionType.Unsupervised) {
            return state.hierarchicalSelection.unsavedLevel.points.size
        } else {
            return state.freeSelection.points.size
        }
    }
)

export const selectSelectionPoints = createSelector(
    selectSelectionState,
    (state: SelectionState): Set<number> => {
        if (state.type == SelectionType.Hierarchical) {
            return state.hierarchicalSelection.unsavedLevel.points
        } else if (state.type == SelectionType.Unsupervised) {
            return state.hierarchicalSelection.unsavedLevel.points
        } else {
            return state.freeSelection.points
        }
    }
)

export const selectHierarchicalSelectionIsRoot = createSelector(
    selectSelectionState,
    (state: SelectionState): boolean => {
        return state.hierarchicalSelection.selectedHierarchyName === undefined
    }
)

export const selectUnsupervisedSelectionIsRoot = createSelector(
    selectSelectionState,
    (state: SelectionState): boolean => {
        return state.unsupervisedSelection.selectedUnsupervisedName === undefined
    }
)

export const selectCaseHasSavedFreeSelections = createSelector(
    selectSelectionState,
    selectCaseSegObjectPropertyMap,
    (selectionState: SelectionState, caseSegObjectPropertyMap: SegObjectPropertyMap): boolean => {
        const caseSegSource = caseSegObjectPropertyMap?.segSource
        if (!caseSegSource) return false

        // Filter savedFreeSelections to match the segSource
        const filteredSavedFreeSelections = Array.from(selectionState.savedFreeSelections).filter(
            selection => selection[1].segSource === caseSegSource
        )
        return filteredSavedFreeSelections.length > 0
    }
)

export const selectCaseHasSavedHierarchicalSelections = createSelector(
    selectSelectionState,
    selectCaseSegObjectPropertyMap,
    (selectionState: SelectionState, caseSegObjectPropertyMap: SegObjectPropertyMap): boolean => {
        const caseSegSource = caseSegObjectPropertyMap?.segSource
        if (!caseSegSource) return false

        const filteredSavedHierarchicalSelections = Array.from(
            selectionState.savedHierarchicalSelections
        ).filter(selection => selection[1].segSource === caseSegSource)
        return filteredSavedHierarchicalSelections.length > 0
    }
)

export const selectCaseHasSavedAutoCellTypeDetections = createSelector(
    selectSelectionState,
    selectCaseSegObjectPropertyMap,
    (selectionState: SelectionState, caseSegObjectPropertyMap: SegObjectPropertyMap): boolean => {
        const caseSegSource = caseSegObjectPropertyMap?.segSource
        if (!caseSegSource) return false

        const filteredSavedUnsupervisedSelections = Array.from(
            selectionState.savedUnsupervisedSelections
        ).filter(selection => selection[1].segSource === caseSegSource)
        return filteredSavedUnsupervisedSelections.length > 0
    }
)

export const selectFreeSelectionName = createSelector(
    selectSelectionState,
    (state: SelectionState): string | undefined => state.freeSelection.name
)

export const selectFreeSelectionColor = createSelector(
    selectSelectionState,
    (state: SelectionState): string | undefined => state.freeSelection.annotationColor
)

export const selectHierarchicalSelectionRootName = createSelector(
    selectSelectionState,
    (state: SelectionState): string | undefined => state.hierarchicalSelection.name
)

export const selectSavedAutoCellTypeDetectionRootName = createSelector(
    selectSelectionState,
    (state: SelectionState): string | undefined => state.unsupervisedSelection.name
)

export const selectHierarchicalSelectionColor = createSelector(
    selectSelectionState,
    (state: SelectionState): { selectedLevel: string; unsavedLevel: string } | undefined => {
        if (state.type !== SelectionType.Hierarchical || SelectionType.Unsupervised) return
        if (!state.hierarchicalSelection.children) return
        const selectedHierarchyName = state.hierarchicalSelection.selectedHierarchyName
        const selectedHierarchy = getSelectedHierarchy(state.hierarchicalSelection.children)

        return {
            selectedLevel: selectedHierarchy ? selectedHierarchy.annotationColor : undefined,
            unsavedLevel: state.hierarchicalSelection.unsavedLevel.annotationColor,
        }

        function getSelectedHierarchy(children: any): any {
            if (!children) return
            for (const c of children) {
                if (c.name === selectedHierarchyName) {
                    return c
                }
                const selectedHierarchy = getSelectedHierarchy(c.children)
                if (selectedHierarchy) {
                    return selectedHierarchy
                }
            }
            return undefined
        }
    }
)

export const selectUnsupervisedSelectionColor = createSelector(
    selectSelectionState,
    (state: SelectionState): { selectedLevel: string; unsavedLevel: string } | undefined => {
        if (state.type !== SelectionType.Unsupervised) return
        if (!state.unsupervisedSelection.children) return
        const selectedUnsupervisedName = state.unsupervisedSelection.selectedUnsupervisedName
        const selectedUnsupervised = getSelectedUnsupervised(state.unsupervisedSelection.children)

        return {
            selectedLevel: selectedUnsupervised ? selectedUnsupervised.annotationColor : undefined,
            unsavedLevel: state.unsupervisedSelection.unsavedLevel.annotationColor,
        }

        function getSelectedUnsupervised(children: any): any {
            if (!children) return
            for (const c of children) {
                if (c.name === selectedUnsupervisedName) {
                    return c
                }
                const selectedUnsupervised = getSelectedUnsupervised(c.children)
                if (selectedUnsupervised) {
                    return selectedUnsupervised
                }
            }
            return undefined
        }
    }
)

export const selectHierarchicalSelectionNodeItem = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionTreeItem => {
        if (state.hierarchicalSelection.selectedHierarchyName) {
            return {
                name: state.hierarchicalSelection.selectedHierarchyName,
                level: getSelectionTreeItemLevel(
                    state.hierarchicalSelection,
                    state.hierarchicalSelection.selectedHierarchyName
                ),
                size: 0,
            }
        } else {
            return defaultHierarchicalSelectionNodeItem
        }
    }
)

export const selectUnsupervisedSelectionNodeItem = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionTreeItem => {
        if (state.unsupervisedSelection.selectedUnsupervisedName) {
            return {
                name: state.unsupervisedSelection.selectedUnsupervisedName,
                level: getSelectionTreeItemLevel(
                    state.unsupervisedSelection,
                    state.unsupervisedSelection.selectedUnsupervisedName
                ),
                size: 0,
            }
        } else {
            return defaultHierarchicalSelectionNodeItem
        }
    }
)

export const selectSavedFreeSelectionNames = createSelector(
    selectSelectionState,
    selectCaseSegObjectPropertyMap,
    (selectionState: SelectionState): string[] => {
        return Array.from(selectionState.savedFreeSelections)
            .map(([key]) => key)
            .sort((a, b) => a.localeCompare(b)) // Extract keys
    }
)

export const selectSavedFreeSelectionNamesBySegSource = createSelector(
    selectSelectionState,
    selectCaseSegObjectPropertyMap,
    (selectionState: SelectionState, caseSegObjectPropertyMap: SegObjectPropertyMap): string[] => {
        return Array.from(selectionState.savedFreeSelections.values())
            .filter(selection => {
                return selection.segSource === caseSegObjectPropertyMap?.segSource
            })
            .map(selection => selection.name!)
            .sort((a: string, b: string) => a.localeCompare(b))
    }
)

export const selectSavedHierarchicalSelectionRootNames = createSelector(
    selectSelectionState,
    selectCaseSegObjectPropertyMap,
    (selectionState: SelectionState, caseSegObjectPropertyMap: SegObjectPropertyMap): string[] => {
        const caseSegSource = caseSegObjectPropertyMap?.segSource
        if (!caseSegSource) return []
        const filteredSavedHierarchicalSelections = Array.from(
            selectionState.savedHierarchicalSelections
        ).filter(
            ([, value]) => value.segSource === caseSegSource // Destructure and filter by segSource
        )
        return filteredSavedHierarchicalSelections
            .map(([key]) => key)
            .filter(key => !key.toLowerCase().includes('unsupervised selections'))
    }
)

export const selectSavedAutoCellTypeDetectionRootNames = createSelector(
    selectSelectionState,
    selectCaseSegObjectPropertyMap,
    (selectionState: SelectionState, caseSegObjectPropertyMap: SegObjectPropertyMap): string[] => {
        const caseSegSource = caseSegObjectPropertyMap?.segSource
        if (!caseSegSource) return []
        const filteredSavedHierarchicalSelections = Array.from(
            selectionState.savedHierarchicalSelections
        ).filter(([, value]) => value.segSource === caseSegSource)
        return filteredSavedHierarchicalSelections
            .map(([key]) => key)
            .filter(key => key.toLowerCase().includes('unsupervised selections'))
    }
)

export const selectSavedHierarchicalSelectionNodeItems = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionTreeItem[] => {
        if (!state.hierarchicalSelection.name) return []
        const selection = state.savedHierarchicalSelections.get(state.hierarchicalSelection.name)
        if (selection) {
            return getAllChildrenSelectionTreeItems(selection)
        } else {
            return []
        }
    }
)

export const selectAllSavedHierarchicalSelectionNodeItems = createSelector(
    selectSelectionState,
    (state: SelectionState): { groupName: string; tree: SelectionTreeItem[] }[] => {
        let currentSelections: string[] = Array.from(state.savedHierarchicalSelections.keys())
        if (state.hierarchicalSelection.unsavedLevel.points.size > 0) {
            currentSelections = [...currentSelections]
        }

        return currentSelections.map(name => {
            const selection = state.savedHierarchicalSelections.get(name)
            if (selection) {
                return { groupName: name, tree: getAllChildrenSelectionTreeItems(selection) }
            } else {
                return { groupName: name, tree: [] }
            }
        })
    }
)

export const selectSavedUnsupervisedSelectionNodeItems = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionTreeItem[] => {
        if (!state.unsupervisedSelection.name) return []
        const selection = state.savedUnsupervisedSelections.get(state.unsupervisedSelection.name)
        if (selection) {
            return getAllChildrenSelectionTreeItems(selection)
        } else {
            return []
        }
    }
)

export const selectAllSavedUnsupervisedSelectionNodeItems = createSelector(
    selectSelectionState,
    (state: SelectionState): { groupName: string; tree: SelectionTreeItem[] }[] => {
        let currentSelections: string[] = Array.from(state.savedUnsupervisedSelections.keys())
        if (state.unsupervisedSelection.unsavedLevel.points.size > 0) {
            currentSelections = [...currentSelections]
        }

        return currentSelections.map(name => {
            const selection = state.savedUnsupervisedSelections.get(name)
            if (selection) {
                return { groupName: name, tree: getAllChildrenSelectionTreeItems(selection) }
            } else {
                return { groupName: name, tree: [] }
            }
        })
    }
)

export const selectSavedAndCurrentFreeSelections = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionsForCaseList => {
        let currentSelection: FreeSelection | null = state.freeSelection

        if (!state.freeSelection.modified) {
            currentSelection = null
        }
        return currentSelection
            ? [currentSelection, ...state.savedFreeSelections.values()]
            : [...state.savedFreeSelections.values()]
    }
)

export const selectDisplayedOnScatterPlot = createSelector(
    selectSelectionState,
    (state: SelectionState): any => {
        return state.displayedOnScatterPlot
    }
)

export const selectSavedAndCurrentFreeSelectionsAsLayers = (
    store: Store
): Selector<SelectionState, SelectionLayer[]> => {
    let lastResult: SelectionLayer[] = []
    return createSelector(selectSelectionState, (state: SelectionState): SelectionLayer[] => {
        let currentSelection: SelectionLayerInfo | null = {
            ...state.freeSelection,
            type: SelectionType.Free,
            rootName: undefined,
        }
        const savedSelections: SelectionLayerInfo[] = [...state.savedFreeSelections.values()]
            .filter(selection => selection.name)
            .map(selection => ({
                ...selection,
                modified:
                    selection.name === state.freeSelection.name && state.freeSelection.modified,
                type: SelectionType.Free,
                rootName: undefined,
            }))
            .sort((a, b) => a.name!.localeCompare(b.name!))

        if (
            !state.freeSelection.modified ||
            !savedSelections.every(selection => !selection.modified)
        ) {
            currentSelection = null
        }

        const displaySelections: SelectionLayerInfo[] = currentSelection
            ? [currentSelection, ...savedSelections]
            : savedSelections

        const displayLayers = convertSelectionLayerInfoToSelectionLayers(
            displaySelections,
            lastResult,
            store
        )
        lastResult = displayLayers
        return displayLayers
    })
}

export const selectSavedAndCurrentHierarchicalSelections = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionsForCaseList => {
        let currentSelections: HierarchicalSelectionNode[] | null = []

        if (state.hierarchicalSelection.unsavedLevel.points.size > 0) {
            currentSelections = [
                {
                    ...state.hierarchicalSelection.unsavedLevel,
                    segSource: state.hierarchicalSelection.segSource,
                    children: undefined,
                },
            ]
        }

        const savedSelections: HierarchicalSelectionNode[] = [
            ...state.savedHierarchicalSelections.values(),
        ]
            .map(selection => getAllChildren(selection))
            .flat()
            .map(selection => ({
                ...selection,
                modified: false,
            }))

        return currentSelections ? [...currentSelections, ...savedSelections] : [...savedSelections]
    }
)

export const selectSavedAndCurrentHierarchicalSelectionsAsLayers = (
    store: Store
): Selector<SelectionState, SelectionLayer[]> => {
    let lastResult: SelectionLayer[] = []
    return createSelector(selectSelectionState, (state: SelectionState): SelectionLayer[] => {
        let currentSelections: SelectionLayerInfo[] | null = []

        if (state.hierarchicalSelection.unsavedLevel.points.size > 0) {
            currentSelections = [
                {
                    ...state.hierarchicalSelection.unsavedLevel,
                    type: SelectionType.Hierarchical,
                    rootName: state.hierarchicalSelection.name,
                },
            ]
        }

        const savedSelections: SelectionLayerInfo[] = [
            ...state.savedHierarchicalSelections.values(),
        ]
            .map(selectionRoot => {
                return getAllChildren(selectionRoot).map(selection => ({
                    ...selection,
                    modified: false,
                    type: SelectionType.Hierarchical,
                    rootName: selectionRoot.name,
                }))
            })
            .flat()

        const displaySelections: SelectionLayerInfo[] = [...currentSelections, ...savedSelections]

        const displayLayers = convertSelectionLayerInfoToSelectionLayers(
            displaySelections,
            lastResult,
            store
        )
        lastResult = displayLayers
        return displayLayers
    })
}

export const selectSavedAndCurrentUnsupervisedSelectionsAsLayers = (
    store: Store
): Selector<SelectionState, SelectionLayer[]> => {
    let lastResult: SelectionLayer[] = []
    return createSelector(selectSelectionState, (state: SelectionState): SelectionLayer[] => {
        let currentSelections: SelectionLayerInfo[] | null = []

        if (state.unsupervisedSelection.unsavedLevel.points.size > 0) {
            currentSelections = [
                {
                    ...state.unsupervisedSelection.unsavedLevel,
                    type: SelectionType.Unsupervised,
                    rootName: state.unsupervisedSelection.name,
                },
            ]
        }

        const savedSelections: SelectionLayerInfo[] = [
            ...state.savedUnsupervisedSelections.values(),
        ]
            .map(selectionRoot => {
                return getAllChildren(selectionRoot).map(selection => ({
                    ...selection,
                    modified: false,
                    type: SelectionType.Unsupervised,
                    rootName: selectionRoot.name,
                }))
            })
            .flat()

        const displaySelections: SelectionLayerInfo[] = [...currentSelections, ...savedSelections]

        const displayLayers = convertSelectionLayerInfoToSelectionLayers(
            displaySelections,
            lastResult,
            store
        )
        lastResult = displayLayers
        return displayLayers
    })
}

const convertSelectionLayerInfoToSelectionLayers = (
    displaySelections: SelectionLayerInfo[],
    lastResult: SelectionLayer[],
    store: Store
): SelectionLayer[] => {
    return displaySelections.map(selection => {
        // this runs during every opacity/color/visibility change
        const existingLayer = lastResult.find(
            (l: SelectionLayer) => l.name === selection.name && l.groupName === selection.rootName
        )
        if (existingLayer) {
            // update observables instead of re-creating the layer
            existingLayer.updateFromStore(selection)
            return existingLayer
        } else {
            return new SelectionLayer(selection, store)
        }
    })
}

export const selectFilterPoints = createSelector(
    selectSelectionState,
    (state: SelectionState): FilterPoints => {
        if (state.type === SelectionType.Free) {
            return { pointsThisLevel: null, pointsLowerLevels: null }
        } else {
            if (state.hierarchicalSelection.selectedHierarchyName == undefined) {
                return {
                    pointsThisLevel: null,
                    pointsLowerLevels: getAllChildrenPointsInRootOrNode(
                        state.hierarchicalSelection
                    ),
                }
            }
            const selection = findSelectionNodeInRoot(
                state.hierarchicalSelection,
                state.hierarchicalSelection.selectedHierarchyName
            )
            return {
                pointsThisLevel: selection ? selection.points : null,
                pointsLowerLevels: selection ? getAllChildrenPointsInRootOrNode(selection) : null,
            }
        }
    }
)

export const selectDisplayedSelectionsOnScatterPlot = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionBase[] => {
        return state.displayedOnScatterPlot
    }
)

export interface FilterPoints {
    pointsThisLevel: Set<number> | null
    pointsLowerLevels: Set<number> | null
}
